import React from 'react';

type ValidatorFn = (event: React.KeyboardEvent<HTMLInputElement>) => void;

export const blockSensitiveCharacter = (
  event: React.KeyboardEvent<HTMLInputElement>,
) => {
  // CLS allows: Letters, numbers, space, _ - . ' @ / ( ) &
  const validator = /[^a-zA-Z0-9\s_\-.'@/()&]/g;

  const target = event.currentTarget;
  if (validator.test(target.value)) {
    // Remove all disallowed characters
    target.value = target.value.replace(validator, '');
  }
};

export const compositeValidators = (
  ...validators: ValidatorFn[]
): ValidatorFn => {
  return (event) => {
    return validators.every((validator) => validator(event));
  };
};

export const emailCharacter = (event: React.FormEvent<HTMLInputElement>) => {
  const validator = /[-`~<>;:"[\]|{}()=+&#$%^*!?]/g;
  const input = event.currentTarget;

  if (validator.test(input.value)) {
    // Remove all invalid characters
    input.value = input.value.replace(validator, '');
  }
};

export const addressCharacter = (event: React.FormEvent<HTMLInputElement>) => {
  // CLS allows: Letters, numbers, space, - . @ ( ) / ' # &
  const validator = /[^a-zA-Z0-9\s,.\-@()/'#&]/g;
  const input = event.currentTarget;

  if (validator.test(input.value)) {
    // Remove all invalid characters
    input.value = input.value.replace(validator, '');
  }
};

export const alphabetOnly = (event: React.KeyboardEvent<HTMLInputElement>) => {
  const validator = /[1234567890`@~<>;:"[-\]|{}(/.,')=_+&#$%^*!?]/;
  if (validator.test(event.key)) {
    event.preventDefault();
  }
};

export const numericOnly = (event: React.FormEvent<HTMLInputElement>) => {
  const input = event.currentTarget;
  const onlyNumbers = input.value.replace(/\D/g, '');
  if (input.value !== onlyNumbers) {
    event.preventDefault();
    input.value = onlyNumbers;
  }
};

export const numericWithDecimal = (
  event: React.FormEvent<HTMLInputElement>,
) => {
  const input = event.currentTarget;
  // Allow digits and at most one decimal point with up to 2 decimal places
  let sanitized = input.value.replace(/[^0-9.]/g, '');
  // Remove extra decimal points (keep only the first one)
  const parts = sanitized.split('.');
  if (parts.length > 2) {
    sanitized = parts[0] + '.' + parts.slice(1).join('');
  }
  // Limit to 2 decimal places
  if (parts.length === 2 && parts[1].length > 2) {
    sanitized = parts[0] + '.' + parts[1].slice(0, 2);
  }
  if (input.value !== sanitized) {
    event.preventDefault();
    input.value = sanitized;
  }
};

export const alphaNumericOnly = (
  event: React.KeyboardEvent<HTMLInputElement>,
) => {
  const validator = /[-`@~<>;:"[-\]|{}(/.,')=_+&#$%^*!?]/;
  if (validator.test(event.key)) {
    event.preventDefault();
  }
};

export const alphaNumericWithSpecialChars = (
  event: React.KeyboardEvent<HTMLInputElement>,
) => {
  const allowedPattern = /^[a-zA-Z0-9 \-.,'()/&]$/;
  if (event.key.length === 1 && !allowedPattern.test(event.key)) {
    event.preventDefault();
  }
};

export const isValidNric = (value: string) => {
  const icNumRegex = /[0-9]{12}/;

  if (!icNumRegex.test(value)) {
    return 'errors.invalidNric';
  }

  const nricStateCode = value.slice(6, 8);

  const isValidNricStateCode = nricStateCode !== '00';
  if (!isValidNricStateCode) {
    return 'errors.invalidStateCode';
  }
  const nricLast4Digits = value.slice(8, 12);
  if (nricLast4Digits === '0000') {
    return 'errors.invalidLast4Digits';
  }

  return true;
};

export const preventLeadingZero = (
  field: 'mobile' | 'income',
): ((event: React.KeyboardEvent<HTMLInputElement>) => boolean) => {
  return (event) => {
    const input = event.currentTarget.value;
    const nextValue = input + event.key;

    if (field === 'mobile') {
      if (/^(0|60)/.test(nextValue)) {
        event.preventDefault();
        return false;
      }
    } else if (field === 'income') {
      if (input === '0' && event.key !== 'Backspace' && event.key !== '.') {
        event.preventDefault();
        return false;
      }
      if (input === '' && event.key === '0') {
        return true;
      }
      if (/^0\d/.test(nextValue)) {
        event.preventDefault();
        return false;
      }
    }

    return true;
  };
};

export const capitalizeEachWord = (value: string): string =>
  value.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export const capitalizeWords = (value: string): string => {
  return value.replace(/\b\w+/g, (word) => {
    const lowerWord = word.toLowerCase();

    if (lowerWord === 'cm' || lowerWord === 'kg') {
      return lowerWord;
    }

    if (word.toUpperCase() === 'RM') {
      return 'RM';
    }

    if (word.toUpperCase() === 'NRIC') {
      return 'NRIC';
    }

    if (lowerWord === 'mykid') {
      return 'MyKid';
    }

    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
};

export const validateHeight = (value: number): boolean => {
  const regex = /^(?:[4-9]\d|1\d{2}|200)$/;
  return regex.test(String(value));
};

export const validateWeight = (value: number): boolean => {
  const regex = /^(?:[1-9]\d?|[1-2]\d{2}|300)$/;
  return regex.test(String(value));
};

export const promoCodeKeyDown: ValidatorFn = (event) => {
  const { key, ctrlKey, metaKey, altKey } = event;

  // Allow shortcuts (copy/paste/select all), etc.
  if (ctrlKey || metaKey || altKey) return;

  // Allow control/navigation keys
  const allowedNonCharKeys = new Set([
    'Backspace',
    'Delete',
    'Tab',
    'Enter',
    'Escape',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ]);
  if (allowedNonCharKeys.has(key)) return;

  if (/^[a-zA-Z0-9 ]$/.test(key)) return;

  event.preventDefault();
};

export const promoCodeSanitize = (event: React.FormEvent<HTMLInputElement>) => {
  const input = event.currentTarget;

  const cleaned = (input.value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9 ]+/g, '')
    .replace(/\s+/g, ' ')
    .trimStart();

  if (input.value !== cleaned) {
    input.value = cleaned;
  }
};
